// @ts-nocheck -- Wave H typing debt: renamed to TypeScript in b59aeab4 without
// being typed (47 strict errors at 94e4d877). As .jsx it was never type-checked,
// so this keeps its pre-rename status while every other file stays gated.
// Ledger: docs/typescript-migration/WAVE-H-TYPECHECK-DEBT.md
/**
 * usePhotoAutoAssign — the Stage 4 Photo Auto-Assign seam.
 *
 * TS-060 (DEC-COUNCIL-015): `dispatchPhotoAutoAssign`, `retryPhotoAssignment`,
 * `getStage5PhotoGate`, and `ensureStage5PhotoGate` moved VERBATIM from App.jsx
 * behind a deps object. The hook owns NO pipeline state — every state value,
 * setter, ref, and helper threads in from App.jsx per the useQuoteEditor pattern.
 */

import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { acquirePaidAuthoringRuntimeLease } from '../utils/paid-authoring-lease';
import { fingerprintWmsAssistantBaseline } from '../utils/wms-assistant-proposal-safety';
import {
    autoAssignPhotosToScope,
    applyAutoAssignResult,
    selectAssignablePhotos,
    getClientFacingPhoto,
    resolveClientPhotoDataUrl,
} from '../services/photo.js';
import { HeroImageCompressor } from '../services/image';
import { resolveCanonicalSavedHero } from '../services/quote-hero-persistence';
import { pickModelForTask } from '../services/model-selector';
import {
    assessAttendedStage5PhotoGate,
    assignablePhotoCoverage,
    buildSavedPhotoDispositions,
    canPersistCompletedStage4PhotoReview,
    photoAssignmentRequestFingerprint,
    photoAssignmentReceiptFingerprint,
    resolvePhotoAutoAssignSingleflight,
    resolvePhotoAssignmentSaveFailure,
    resolvePhotoAssignmentSaveSuccess,
    scopeFingerprintForAutoDraft,
    hasFinalStage3QuoteInputs,
    waitForPhotoAutoAssignRetryEligibility,
} from '../utils/auto-pipeline';
import {
    captureProjectRunContext,
    createStaleProjectRunError,
    isProjectRunContextCurrent,
    type ProjectRunContext,
} from '../utils/project-run-context';
import {
    hasInvalidatedAcceptedZeroScopePhotoReview,
    markAcceptedZeroScopePhotoReview,
    seenPhotoIdsForLoadedProject,
} from '../utils/photo-reopen-seed';

export interface DispatchPhotoAutoAssignOptions {
    trigger?: string;
    photosOverride?: any[];
    scopeOverride?: any[];
    requestRunContext?: any;
    expectedWmsBaselineFingerprint?: string | null;
    expectedWmsRegenerationSourceFingerprint?: string | null;
    getCurrentWmsRegenerationSourceFingerprint?: (() => string | null) | null;
}

export interface UsePhotoAutoAssignDeps {
    apiKey: string;
    model: string;
    photos: any[];
    scope: any[];
    stage: number | string;
    currentPage: string;
    photoAnalysis: any;
    projectWMS: any;
    projectCostingSheet: any;
    selectedHeroImage: any;
    photoAssignBusy: boolean;
    photoAssignmentReceiptRevision: number;
    currentProjectId: string | null;
    setScope: Dispatch<SetStateAction<any>>;
    setPhotoAssignBusy: Dispatch<SetStateAction<boolean>>;
    setSelectedHeroImage: Dispatch<SetStateAction<any>>;
    setNavMessage: Dispatch<SetStateAction<any>>;
    publishPhotoAssignmentReceipt: (receipt: any) => void;
    photoAssignmentForensicObservationsRef: MutableRefObject<any>;
    photosRef: MutableRefObject<any[]>;
    scopeRef: MutableRefObject<any[]>;
    stageRef: MutableRefObject<number | string>;
    projectWMSRef: MutableRefObject<any>;
    projectCostingSheetRef: MutableRefObject<any>;
    selectedHeroImageRef: MutableRefObject<any>;
    currentProjectIdRef: { current: any };
    projectLoadGenerationRef: { current: any };
    paintedProjectLoadContextRef: { current: { projectId: any; loadGeneration: any } };
    projectAbortRef: MutableRefObject<AbortController>;
    autoAssignAbortRef: MutableRefObject<AbortController | null>;
    aiPipelineHaltedRef: MutableRefObject<boolean>;
    openedForReviewRef: MutableRefObject<boolean>;
    lastAssignedPhotoIdsRef: MutableRefObject<Set<any>>;
    photoAssignBusyRef: MutableRefObject<boolean>;
    photoAssignmentReceiptRef: MutableRefObject<any>;
    photoReviewSaveInFlightRef: MutableRefObject<boolean>;
    photoAutoAssignCompletionRef: MutableRefObject<any>;
    saveCurrentProjectRef: { current: any };
}

export function usePhotoAutoAssign(deps: UsePhotoAutoAssignDeps) {
    const {
        apiKey,
        model,
        photos,
        scope,
        stage,
        currentPage,
        photoAnalysis,
        projectWMS,
        projectCostingSheet,
        selectedHeroImage,
        photoAssignBusy,
        photoAssignmentReceiptRevision,
        currentProjectId,
        setScope,
        setPhotoAssignBusy,
        setSelectedHeroImage,
        setNavMessage,
        publishPhotoAssignmentReceipt,
        photoAssignmentForensicObservationsRef,
        photosRef,
        scopeRef,
        stageRef,
        projectWMSRef,
        projectCostingSheetRef,
        selectedHeroImageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        projectAbortRef,
        autoAssignAbortRef,
        aiPipelineHaltedRef,
        openedForReviewRef,
        lastAssignedPhotoIdsRef,
        photoAssignBusyRef,
        photoAssignmentReceiptRef,
        photoReviewSaveInFlightRef,
        photoAutoAssignCompletionRef,
        saveCurrentProjectRef,
    } = deps;

    const dispatchPhotoAutoAssign = useCallback(async ({
        trigger,
        photosOverride,
        scopeOverride,
        requestRunContext = null,
        expectedWmsBaselineFingerprint = null,
        expectedWmsRegenerationSourceFingerprint = null,
        getCurrentWmsRegenerationSourceFingerprint = null,
    }: DispatchPhotoAutoAssignOptions = {}) => {
        const triggerLabel = trigger || 'unknown';
        const startedAt = Date.now();
        let assignmentCompletionPromise = null;
        let resolveAssignmentCompletion = null;
        // Snapshot inputs immediately so a state change mid-flight can't
        // shift indices under us.
        const photosSnapshot = Array.isArray(photosOverride) ? photosOverride : photos;
        const scopeSnapshot = Array.isArray(scopeOverride) ? scopeOverride : scope;
        const assignablePhotosSnapshot = selectAssignablePhotos(photosSnapshot).photos;
        const forensicObservations = photoAssignmentForensicObservationsRef.current;
        // One request retains the exact project/load/signal that supplied its
        // photos and scope even when it queues behind another assignment. A
        // retry may never recompute ownership from whichever project happens
        // to be current after that await.
        const assignmentRunContext = requestRunContext || captureProjectRunContext({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const assignmentMatchesPaintedProject = () => (
            isProjectRunContextCurrent(assignmentRunContext, {
                projectId: currentProjectIdRef.current,
                loadGeneration: projectLoadGenerationRef.current,
                signal: projectAbortRef.current?.signal || null,
            })
            && paintedProjectLoadContextRef.current.projectId === assignmentRunContext.projectId
            && Number(paintedProjectLoadContextRef.current.loadGeneration) === assignmentRunContext.loadGeneration
        );
        const assignmentMatchesExpectedWms = () => (
            !expectedWmsBaselineFingerprint
            || fingerprintWmsAssistantBaseline(projectWMSRef.current) === expectedWmsBaselineFingerprint
        );
        let liveExpectedRegenerationSourceFingerprint = expectedWmsRegenerationSourceFingerprint;
        const assignmentMatchesExpectedRegenerationSource = () => (
            !liveExpectedRegenerationSourceFingerprint
            || (typeof getCurrentWmsRegenerationSourceFingerprint === 'function'
                && getCurrentWmsRegenerationSourceFingerprint() === liveExpectedRegenerationSourceFingerprint)
        );
        const advanceExpectedRegenerationSourceFingerprint = () => {
            if (!liveExpectedRegenerationSourceFingerprint
                || typeof getCurrentWmsRegenerationSourceFingerprint !== 'function') return;
            liveExpectedRegenerationSourceFingerprint = getCurrentWmsRegenerationSourceFingerprint();
        };
        const requestedAssignment = {
            projectId: assignmentRunContext.projectId,
            loadGeneration: assignmentRunContext.loadGeneration,
            fingerprint: photoAssignmentRequestFingerprint(
                assignablePhotosSnapshot,
                scopeSnapshot,
                forensicObservations,
            ),
        };
        if (!apiKey) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): no apiKey`);
            return;
        }
        if (!Array.isArray(photosSnapshot) || photosSnapshot.length === 0) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): no photos`);
            return;
        }
        if (assignablePhotosSnapshot.length === 0) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): no assignable site photos`);
            return;
        }
        if (!Array.isArray(scopeSnapshot) || scopeSnapshot.length === 0) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): no scope yet`);
            return;
        }
        // A newly-created project adopts its durable id through React state;
        // the render-level painted-context seam stamps that id before effects
        // or user callbacks can dispatch. Still fail closed here so no receipt,
        // AI spend or prior-run cancellation can occur against an unpainted id.
        if (!assignmentRunContext.projectId || !assignmentMatchesPaintedProject()) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): project identity has not painted`);
            return { status: 'stale-project', scope: scopeSnapshot };
        }
        if (!assignmentMatchesExpectedWms()) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): Work Method changed before photo preparation`);
            return { status: 'stale-wms', scope: scopeSnapshot };
        }
        if (!assignmentMatchesExpectedRegenerationSource()) {
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): regeneration source changed before photo preparation`);
            return { status: 'stale-source', scope: scopeSnapshot };
        }
        if (photoAssignBusyRef.current) {
            const active = photoAutoAssignCompletionRef.current;
            const disposition = resolvePhotoAutoAssignSingleflight(active, requestedAssignment);
            if (disposition === 'join-identical') {
                console.info(`[photo-auto-assign] Joining (${triggerLabel}): identical assignment already in flight`);
                await active.promise;
                if (!assignmentMatchesPaintedProject()) {
                    return { status: 'stale-project', scope: scopeSnapshot };
                }
                if (!assignmentMatchesExpectedWms()) {
                    return { status: 'stale-wms', scope: scopeSnapshot };
                }
                if (!assignmentMatchesExpectedRegenerationSource()) {
                    return { status: 'stale-source', scope: scopeSnapshot };
                }
                return {
                    outcome: 'joined',
                    scope: Array.isArray(scopeRef.current) ? scopeRef.current : scopeSnapshot,
                    receipt: photoAssignmentReceiptRef.current,
                };
            }
            if (disposition === 'wait-and-retry') {
                console.info(`[photo-auto-assign] Waiting (${triggerLabel}): same project has different scope/photo assignment in flight`);
                const retryEligible = await waitForPhotoAutoAssignRetryEligibility({
                    active,
                    requestContext: assignmentRunContext,
                    getCurrentContext: () => ({
                        projectId: currentProjectIdRef.current,
                        loadGeneration: projectLoadGenerationRef.current,
                        signal: projectAbortRef.current?.signal || null,
                    }),
                });
                if (!retryEligible
                    || !assignmentMatchesPaintedProject()
                    || !assignmentMatchesExpectedWms()
                    || !assignmentMatchesExpectedRegenerationSource()) {
                    console.info(`[photo-auto-assign] Cancelled queued retry (${triggerLabel}): project changed while waiting`);
                    return { status: 'stale-project', scope: scopeSnapshot };
                }
                return dispatchPhotoAutoAssign({
                    trigger,
                    photosOverride: photosSnapshot,
                    scopeOverride: scopeSnapshot,
                    requestRunContext: assignmentRunContext,
                    expectedWmsBaselineFingerprint,
                    expectedWmsRegenerationSourceFingerprint: liveExpectedRegenerationSourceFingerprint,
                    getCurrentWmsRegenerationSourceFingerprint,
                });
            }
            console.info(`[photo-auto-assign] Skipped (${triggerLabel}): another project assignment is in-flight`);
            return { status: 'busy-other-project', scope: scopeSnapshot };
        }

        assignmentCompletionPromise = new Promise((resolve) => {
            resolveAssignmentCompletion = resolve;
        });
        photoAutoAssignCompletionRef.current = {
            projectId: assignmentRunContext.projectId,
            loadGeneration: assignmentRunContext.loadGeneration,
            fingerprint: requestedAssignment.fingerprint,
            promise: assignmentCompletionPromise,
        };

        // Mark seen-set BEFORE firing so a re-render mid-call doesn't queue
        // a duplicate dispatch. We add only photo IDs that exist now; photos
        // added later will diff against this set and re-fire.
        const photoIdSet = new Set();
        for (const p of photosSnapshot) {
            const id = p?.id || p?.blobId || p?.s3Key || p?.thumbnailUrl || p?.name;
            if (id) photoIdSet.add(id);
        }
        // Snapshot the previous size for after-the-fact logging.
        const prevSetSize = lastAssignedPhotoIdsRef.current.size;
        // Record the evidence set before the paid call. Terminal outcomes keep
        // this receipt: otherwise an unrelated Stage 4 scope fingerprint
        // change makes the same photos look new and silently spends again.
        // The explicit Retry controls call this dispatcher directly and do not
        // depend on the watcher seen-set, so operator recovery stays available.
        lastAssignedPhotoIdsRef.current = photoIdSet;

        // Cancel any previous auto-assign call (project switch / fresh run).
        // Note: stage navigation does NOT reach here; the only callers are
        // (a) the watcher useEffect, (b) explicit dispatchPhotoAutoAssign
        // calls. Both are safe places to abort the old call because they
        // only fire when there's a meaningful change (new scope or new
        // photos).
        if (autoAssignAbortRef.current) {
            try { autoAssignAbortRef.current.abort('superseded'); } catch {}
        }
        const ctrl = new AbortController();
        autoAssignAbortRef.current = ctrl;
        photoAssignBusyRef.current = true;
        setPhotoAssignBusy(true);

        const assignmentOwnsPaintedProject = () => (
            !ctrl.signal.aborted
            && assignmentMatchesPaintedProject()
            && assignmentMatchesExpectedWms()
            && assignmentMatchesExpectedRegenerationSource()
        );
        const createPhotoAssignmentSourceChangedError = () => {
            const staleSourceError = new Error('The Stage 4 scope or Stage 5 quote changed during photo preparation. Stale photo results were discarded before scope, hero or save writeback.') as Error & { code?: string };
            staleSourceError.name = 'AbortError';
            staleSourceError.code = 'WMS_ASSISTANT_SOURCE_CHANGED';
            return staleSourceError;
        };
        const assertPhotoAssignmentOwnsWriteback = () => {
            if (!assignmentOwnsPaintedProject()) {
                if (!assignmentMatchesExpectedWms()) {
                    const staleWmsError = new Error('The Work Method changed during photo preparation. Stale photo results were discarded before scope, hero or save writeback.') as Error & { code?: string };
                    staleWmsError.name = 'AbortError';
                    staleWmsError.code = 'WMS_ASSISTANT_BASELINE_CHANGED';
                    throw staleWmsError;
                }
                if (!assignmentMatchesExpectedRegenerationSource()) {
                    throw createPhotoAssignmentSourceChangedError();
                }
                throw createStaleProjectRunError('Photo assignment');
            }
        };
        publishPhotoAssignmentReceipt({
            projectId: assignmentRunContext.projectId,
            loadGeneration: assignmentRunContext.loadGeneration,
            status: 'in-flight',
            fingerprint: photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, scopeSnapshot),
            trigger: triggerLabel,
        });

        console.info(`[photo-auto-assign] FIRE (${triggerLabel}) — ${photosSnapshot.length} photos, ${scopeSnapshot.length} scope items, ${forensicObservations?.length || 0} forensic observations, ${photoIdSet.size - prevSetSize} new photo(s) since last run`);

        // Photo assignment can overlap the tail of Stage 3 while the service
        // worker has an update reload queued behind Stage 3's paid-authoring
        // lease. It must own the same shared lease itself: otherwise Stage 3
        // releases the final counted lease, the bootstrap reloads this page,
        // and the still-streaming vision request is recorded as
        // `client_disconnected`. Keep the lease through assignment, hero
        // resolution and the exact-scope persistence receipt.
        const releaseAuthoringLease = acquirePaidAuthoringRuntimeLease();
        try {
            const result = await autoAssignPhotosToScope({
                photos: photosSnapshot,
                scope: scopeSnapshot,
                apiKey,
                model: pickModelForTask('photo-auto-assign', model),
                signal: ctrl.signal,
                projectId: assignmentRunContext.projectId,
                forensicObservations,
            });
            if (!assignmentMatchesExpectedWms() || !assignmentMatchesExpectedRegenerationSource()) {
                assertPhotoAssignmentOwnsWriteback();
            }
            if (!result || ctrl.signal.aborted) {
                // Retain the attempted-photo receipt. Explicit Retry remains
                // available; passive scope churn must not spend again.
                if (assignmentOwnsPaintedProject()) {
                    publishPhotoAssignmentReceipt({
                        projectId: assignmentRunContext.projectId,
                        loadGeneration: assignmentRunContext.loadGeneration,
                        status: 'aborted',
                        fingerprint: photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, scopeSnapshot),
                        trigger: triggerLabel,
                    });
                }
                console.info(`[photo-auto-assign] Aborted or empty result (${triggerLabel}) after ${Date.now() - startedAt}ms`);
                return;
            }
            // Re-read every matching input before consuming any result branch.
            // IDs alone are insufficient ownership: a builder can correct
            // scope wording, photo labels or forensic locators without changing
            // topology while the vision request is in flight. This check must
            // also precede the zero-link receipt save below, not only linked
            // result application.
            const liveAssignablePhotosBeforeApply = selectAssignablePhotos(photosRef.current).photos;
            const liveScopeBeforeApply = Array.isArray(scopeRef.current) ? scopeRef.current : [];
            const liveForensicObservationsBeforeApply = photoAssignmentForensicObservationsRef.current;
            const liveRequestFingerprintBeforeApply = photoAssignmentRequestFingerprint(
                liveAssignablePhotosBeforeApply,
                liveScopeBeforeApply,
                liveForensicObservationsBeforeApply,
            );
            if (!assignmentOwnsPaintedProject()
                || liveRequestFingerprintBeforeApply !== requestedAssignment.fingerprint) {
                if (assignmentMatchesPaintedProject()
                    && assignmentMatchesExpectedWms()
                    && assignmentMatchesExpectedRegenerationSource()
                    && liveRequestFingerprintBeforeApply !== requestedAssignment.fingerprint) {
                    publishPhotoAssignmentReceipt({
                        projectId: assignmentRunContext.projectId,
                        loadGeneration: assignmentRunContext.loadGeneration,
                        status: 'stale-scope',
                        fingerprint: photoAssignmentReceiptFingerprint(
                            liveAssignablePhotosBeforeApply,
                            liveScopeBeforeApply,
                        ),
                        trigger: triggerLabel,
                    });
                }
                console.warn(`[photo-auto-assign] Result discarded (${triggerLabel}) — matching inputs changed between dispatch and apply`);
                return;
            }
            if (result.route?.fallbackApplied) {
                setNavMessage({
                    id: 'photo-auto-assign-fallback',
                    tone: 'warn',
                    icon: 'AlertTriangle',
                    text: 'Photo selection used the fallback Lane after the primary vision route failed. Its validated strongest-photo selection will continue once saved.',
                    dismissible: true,
                });
            } else {
                setNavMessage(prev => (
                    prev?.id === 'photo-auto-assign-failed'
                    || prev?.id === 'photo-auto-assign-fallback'
                    || prev?.id === 'photo-auto-assign-review'
                        ? null
                        : prev
                ));
            }
            if (result.assignments.length === 0) {
                const coverage = assignablePhotoCoverage(liveAssignablePhotosBeforeApply, liveScopeBeforeApply);
                const reviewedDispositions = buildSavedPhotoDispositions({
                    assignablePhotos: liveAssignablePhotosBeforeApply,
                    scopeItems: liveScopeBeforeApply,
                    proposedDispositions: result.dispositions,
                });
                if (assignmentOwnsPaintedProject()) {
                    const reviewedScope = markAcceptedZeroScopePhotoReview(
                        liveScopeBeforeApply,
                        liveAssignablePhotosBeforeApply,
                        new Date().toISOString(),
                        'ai',
                    );
                    const reviewedScopeFingerprint = scopeFingerprintForAutoDraft(reviewedScope);
                    try {
                        if (reviewedScope !== liveScopeBeforeApply) {
                            // Paint only the invisible receipt metadata before
                            // starting the save. Any subsequent builder edit is
                            // then based on this receipt-bearing scope; the
                            // settlement guard below still refuses to repaint
                            // canonical server data over that newer edit.
                            assertPhotoAssignmentOwnsWriteback();
                            scopeRef.current = reviewedScope;
                            setScope(reviewedScope);
                            advanceExpectedRegenerationSourceFingerprint();
                            publishPhotoAssignmentReceipt({
                                projectId: assignmentRunContext.projectId,
                                loadGeneration: assignmentRunContext.loadGeneration,
                                status: 'in-flight',
                                fingerprint: photoAssignmentReceiptFingerprint(liveAssignablePhotosBeforeApply, reviewedScope),
                                assignedPhotos: coverage.assigned,
                                assignablePhotos: coverage.total,
                                dispositions: reviewedDispositions,
                                reviewReason: 'no-confident-links',
                                trigger: triggerLabel,
                            });
                            assertPhotoAssignmentOwnsWriteback();
                            const savedProject = await saveCurrentProjectRef.current?.({
                                scope: reviewedScope,
                                silent: true,
                            }, {
                                runContext: assignmentRunContext,
                                beforeCommit: assertPhotoAssignmentOwnsWriteback,
                            });
                            if (!savedProject) {
                                throw createStaleProjectRunError('Zero-link photo review save');
                            }
                            assertPhotoAssignmentOwnsWriteback();
                            const savedScope = Array.isArray(savedProject.scope) ? savedProject.scope : [];
                            const savedSeenIds = seenPhotoIdsForLoadedProject({
                                photos: liveAssignablePhotosBeforeApply,
                                scope: savedScope,
                            });
                            const receiptWasDurable = liveAssignablePhotosBeforeApply.every((photo) => {
                                const id = photo?.id || photo?.blobId || photo?.s3Key || photo?.thumbnailUrl || photo?.name;
                                return !id || savedSeenIds.has(id);
                            });
                            if (!receiptWasDurable) {
                                throw new Error('Zero-link photo review save did not preserve its exact evidence receipt.');
                            }
                            const successResolution = resolvePhotoAssignmentSaveSuccess({
                                liveScope: scopeRef.current,
                                appliedScope: reviewedScope,
                                appliedScopeFingerprint: reviewedScopeFingerprint,
                                savedScope,
                            });
                            if (successResolution.preservedLiveEdits) {
                                throw createPhotoAssignmentSourceChangedError();
                            }
                            if (successResolution.shouldAdoptCanonical) {
                                assertPhotoAssignmentOwnsWriteback();
                                scopeRef.current = successResolution.scope;
                                setScope(successResolution.scope);
                            }
                            advanceExpectedRegenerationSourceFingerprint();
                        }
                        publishPhotoAssignmentReceipt({
                            projectId: assignmentRunContext.projectId,
                            loadGeneration: assignmentRunContext.loadGeneration,
                            status: 'review-required',
                            fingerprint: photoAssignmentReceiptFingerprint(liveAssignablePhotosBeforeApply, reviewedScope),
                            assignedPhotos: coverage.assigned,
                            assignablePhotos: coverage.total,
                            dispositions: reviewedDispositions,
                            reviewReason: 'no-confident-links',
                            durable: reviewedScope !== liveScopeBeforeApply,
                            trigger: triggerLabel,
                        });
                    } catch (persistErr) {
                        if (['WMS_ASSISTANT_BASELINE_CHANGED', 'WMS_ASSISTANT_SOURCE_CHANGED'].includes(String(persistErr?.code || ''))) {
                            throw persistErr;
                        }
                        const failureResolution = resolvePhotoAssignmentSaveFailure({
                            liveScope: scopeRef.current,
                            appliedScope: reviewedScope,
                            appliedScopeFingerprint: reviewedScopeFingerprint,
                            previousScope: liveScopeBeforeApply,
                        });
                        if (failureResolution.shouldRollback && assignmentOwnsPaintedProject()) {
                            scopeRef.current = failureResolution.scope;
                            setScope(failureResolution.scope);
                            advanceExpectedRegenerationSourceFingerprint();
                        }
                        publishPhotoAssignmentReceipt({
                            projectId: assignmentRunContext.projectId,
                            loadGeneration: assignmentRunContext.loadGeneration,
                            status: 'save-failed',
                            fingerprint: photoAssignmentReceiptFingerprint(
                                liveAssignablePhotosBeforeApply,
                                failureResolution.scope,
                            ),
                            assignedPhotos: coverage.assigned,
                            assignablePhotos: coverage.total,
                            dispositions: reviewedDispositions,
                            reviewReason: 'no-confident-links',
                            trigger: triggerLabel,
                        });
                        setNavMessage({
                            id: 'photo-auto-assign-failed',
                            tone: 'error',
                            icon: 'AlertTriangle',
                            text: 'Photo review finished with no confident links, but its result could not be saved. Stage 5 remains available; retry or assign photos manually when storage recovers.',
                            dismissible: true,
                        });
                        console.warn(`[photo-auto-assign] ZERO-LINK SAVE FAILED (${triggerLabel}):`, persistErr?.message || persistErr);
                        return;
                    }
                }
                if (!result.route?.fallbackApplied) {
                    setNavMessage({
                        id: 'photo-auto-assign-review',
                        tone: 'warn',
                        icon: 'AlertTriangle',
                        text: 'Photo assignment found no confident scope links. Review and attach the relevant photos manually in Stage 4.',
                        dismissible: true,
                    });
                }
                console.info(`[photo-auto-assign] No assignments produced (${triggerLabel}) — droppedDocPhotos=${result.droppedDocPhotos || 0}`);
                return {
                    status: 'review-required',
                    scope: Array.isArray(scopeRef.current) ? scopeRef.current : liveScopeBeforeApply,
                    expectedWmsRegenerationSourceFingerprint: liveExpectedRegenerationSourceFingerprint,
                };
            }
            const { nextScope, assignedCount: appliedAssignedCount } = applyAutoAssignResult(
                liveScopeBeforeApply,
                photosSnapshot,
                result.assignments,
            );
            const appliedScopeFingerprint = scopeFingerprintForAutoDraft(nextScope);
            if (!assignmentOwnsPaintedProject()) {
                return;
            }
            assertPhotoAssignmentOwnsWriteback();
            scopeRef.current = nextScope;
            setScope(nextScope);
            advanceExpectedRegenerationSourceFingerprint();
            // Hero photo. Only set if the user hasn't picked one already
            // (the prev-=>-prev||compressed pattern preserves manual choice).
            const heroBeforeAutoSelection = selectedHeroImageRef.current;
            let autoSelectedHeroReference = null;
            let autoSelectedHeroDisplay = null;
            if (
                !ctrl.signal.aborted
                && result.heroPhotoIndex != null
                && result.heroPhotoIndex >= 0
                && result.heroPhotoIndex < photosSnapshot.length
            ) {
                const heroPhoto = photosSnapshot[result.heroPhotoIndex];
                const heroDataUrl = await resolveClientPhotoDataUrl(heroPhoto, assignmentRunContext.projectId || null);
                if (heroDataUrl && assignmentOwnsPaintedProject() && !selectedHeroImageRef.current) {
                    try {
                        const compressed = await HeroImageCompressor.compress(heroDataUrl);
                        if (assignmentOwnsPaintedProject() && !selectedHeroImageRef.current) {
                            assertPhotoAssignmentOwnsWriteback();
                            autoSelectedHeroDisplay = compressed;
                            selectedHeroImageRef.current = compressed;
                            setSelectedHeroImage(prev => prev || compressed);
                            autoSelectedHeroReference = getClientFacingPhoto(heroPhoto);
                        }
                    } catch (e) {
                        if (assignmentOwnsPaintedProject() && !selectedHeroImageRef.current) {
                            assertPhotoAssignmentOwnsWriteback();
                            autoSelectedHeroDisplay = heroDataUrl;
                            selectedHeroImageRef.current = heroDataUrl;
                            setSelectedHeroImage(prev => prev || heroDataUrl);
                            autoSelectedHeroReference = getClientFacingPhoto(heroPhoto);
                        }
                    }
                }
            }

            // The auto quote effect reads the receipt synchronously, so keep it
            // in-flight until the exact image-linked scope has been accepted by
            // the normal durable project save. A failed save restores the prior
            // live scope; otherwise a retry could mistake unsaved React state for
            // durable assignment evidence.
            try {
                assertPhotoAssignmentOwnsWriteback();
                const savedProject = await saveCurrentProjectRef.current?.({
                    scope: nextScope,
                    ...(autoSelectedHeroReference
                        ? { selectedHeroImage: autoSelectedHeroReference }
                        : {}),
                    silent: true,
                }, {
                    runContext: assignmentRunContext,
                    beforeCommit: assertPhotoAssignmentOwnsWriteback,
                });
                if (!savedProject) {
                    throw createStaleProjectRunError('Photo assignment save');
                }
                assertPhotoAssignmentOwnsWriteback();
                const savedScope = Array.isArray(savedProject.scope) ? savedProject.scope : [];
                const successResolution = resolvePhotoAssignmentSaveSuccess({
                    liveScope: scopeRef.current,
                    appliedScope: nextScope,
                    appliedScopeFingerprint,
                    savedScope,
                });
                if (successResolution.preservedLiveEdits) {
                    throw createPhotoAssignmentSourceChangedError();
                }
                if (successResolution.shouldAdoptCanonical) {
                    assertPhotoAssignmentOwnsWriteback();
                    scopeRef.current = successResolution.scope;
                    setScope(successResolution.scope);
                }
                advanceExpectedRegenerationSourceFingerprint();
                const settledScope = successResolution.scope;
                const savedFingerprint = photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, settledScope);
                const liveFingerprint = photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, nextScope);
                if (savedFingerprint !== liveFingerprint) {
                    throw new Error('Photo assignment save did not preserve the linked scope.');
                }
                if (autoSelectedHeroDisplay
                    && selectedHeroImageRef.current === autoSelectedHeroDisplay) {
                    assertPhotoAssignmentOwnsWriteback();
                    const canonicalHero = resolveCanonicalSavedHero(
                        savedProject,
                        autoSelectedHeroReference,
                    );
                    if (!canonicalHero) {
                        throw new Error('Photo assignment save did not return a durable hero reference.');
                    }
                    // Adopt the receipt before publishing Stage 5 readiness.
                    // The quote effect is receipt-gated, so both normal and
                    // provider-fallback generation now see only this portable
                    // object, never the compressed display data URL.
                    selectedHeroImageRef.current = canonicalHero;
                    setSelectedHeroImage(canonicalHero);
                }
                const coverage = assignablePhotoCoverage(assignablePhotosSnapshot, settledScope);
                const savedDispositions = buildSavedPhotoDispositions({
                    assignablePhotos: assignablePhotosSnapshot,
                    scopeItems: settledScope,
                    proposedDispositions: result.dispositions,
                });
                publishPhotoAssignmentReceipt({
                    projectId: assignmentRunContext.projectId,
                    loadGeneration: assignmentRunContext.loadGeneration,
                    status: 'saved',
                    fingerprint: liveFingerprint,
                    assignedPhotos: coverage.assigned,
                    assignablePhotos: coverage.total,
                    dispositions: savedDispositions,
                    assignmentRoute: result.route?.fallbackApplied ? 'fallback' : 'primary',
                    trigger: triggerLabel,
                });
                if (!coverage.complete) {
                    setNavMessage({
                        id: 'photo-auto-assign-selection',
                        tone: result.route?.fallbackApplied ? 'warn' : 'info',
                        icon: result.route?.fallbackApplied ? 'AlertTriangle' : 'CheckCircle',
                        text: `Selected ${coverage.assigned} of ${coverage.total} strongest site photos; ${coverage.total - coverage.assigned} omitted from the quote.`,
                        dismissible: true,
                    });
                }
            } catch (persistErr) {
                if (['WMS_ASSISTANT_BASELINE_CHANGED', 'WMS_ASSISTANT_SOURCE_CHANGED'].includes(String(persistErr?.code || ''))) {
                    throw persistErr;
                }
                if (assignmentOwnsPaintedProject()) {
                    if (autoSelectedHeroDisplay
                        && selectedHeroImageRef.current === autoSelectedHeroDisplay) {
                        selectedHeroImageRef.current = heroBeforeAutoSelection;
                        setSelectedHeroImage(heroBeforeAutoSelection);
                    }
                    const failureResolution = resolvePhotoAssignmentSaveFailure({
                        liveScope: scopeRef.current,
                        appliedScope: nextScope,
                        appliedScopeFingerprint,
                        previousScope: liveScopeBeforeApply,
                    });
                    if (failureResolution.shouldRollback) {
                        assertPhotoAssignmentOwnsWriteback();
                        scopeRef.current = failureResolution.scope;
                        setScope(failureResolution.scope);
                        advanceExpectedRegenerationSourceFingerprint();
                    }
                    const failureCoverage = assignablePhotoCoverage(
                        assignablePhotosSnapshot,
                        failureResolution.scope,
                    );
                    publishPhotoAssignmentReceipt({
                        projectId: assignmentRunContext.projectId,
                        loadGeneration: assignmentRunContext.loadGeneration,
                        status: 'save-failed',
                        fingerprint: photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, failureResolution.scope),
                        assignedPhotos: failureCoverage.assigned,
                        assignablePhotos: failureCoverage.total,
                        preservedLiveEdits: failureResolution.preservedLiveEdits,
                        trigger: triggerLabel,
                    });
                    setNavMessage({
                        id: 'photo-auto-assign-failed',
                        tone: 'error',
                        icon: 'AlertTriangle',
                        text: failureResolution.preservedLiveEdits
                            ? 'Stage 5 remains available; only the photo links are not durably saved. Your newer Stage 4 edits were preserved but remain unsaved. Save or retry when storage recovers.'
                            : 'Stage 5 remains available; only the prepared photo links are not durably saved. Retry photo assignment from Stage 4 when storage recovers.',
                        dismissible: true,
                    });
                }
                console.warn(`[photo-auto-assign] SAVE FAILED (${triggerLabel}):`, persistErr?.message || persistErr);
                return;
            }
            console.info(`[photo-auto-assign] DONE (${triggerLabel}) — ${appliedAssignedCount} assignments applied, droppedDocPhotos=${result.droppedDocPhotos || 0}, hero=${result.heroPhotoIndex ?? 'none'}, ${Date.now() - startedAt}ms`);
            return {
                status: 'saved',
                scope: Array.isArray(scopeRef.current) ? scopeRef.current : nextScope,
                expectedWmsRegenerationSourceFingerprint: liveExpectedRegenerationSourceFingerprint,
            };
        } catch (err) {
            if (['WMS_ASSISTANT_BASELINE_CHANGED', 'WMS_ASSISTANT_SOURCE_CHANGED'].includes(String(err?.code || ''))) {
                throw err;
            }
            if (err?.name === 'AbortError' || /aborted/i.test(err?.message || '')) {
                if (assignmentMatchesPaintedProject()) {
                    publishPhotoAssignmentReceipt({
                        projectId: assignmentRunContext.projectId,
                        loadGeneration: assignmentRunContext.loadGeneration,
                        status: 'aborted',
                        fingerprint: photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, scopeSnapshot),
                        trigger: triggerLabel,
                    });
                }
                console.info(`[photo-auto-assign] Cancelled (${triggerLabel}) after ${Date.now() - startedAt}ms`);
            } else {
                console.warn(`[photo-auto-assign] FAILED (${triggerLabel}) after ${Date.now() - startedAt}ms:`, err?.message || err);
                if (assignmentOwnsPaintedProject()) {
                    publishPhotoAssignmentReceipt({
                        projectId: assignmentRunContext.projectId,
                        loadGeneration: assignmentRunContext.loadGeneration,
                        status: 'failed',
                        fingerprint: photoAssignmentReceiptFingerprint(assignablePhotosSnapshot, scopeSnapshot),
                        trigger: triggerLabel,
                    });
                }
                setNavMessage({
                    id: 'photo-auto-assign-failed',
                    tone: 'error',
                    icon: 'AlertTriangle',
                    text: 'Photo assignment failed on both Lanes. No links were applied; retry in Stage 4 or assign photos manually before final review.',
                    dismissible: true,
                });
            }
        } finally {
            try {
                if (autoAssignAbortRef.current === ctrl) {
                    autoAssignAbortRef.current = null;
                    photoAssignBusyRef.current = false;
                    setPhotoAssignBusy(false);
                }
            } finally {
                try {
                    resolveAssignmentCompletion?.();
                    if (photoAutoAssignCompletionRef.current?.promise === assignmentCompletionPromise) {
                        photoAutoAssignCompletionRef.current = null;
                    }
                } finally {
                    releaseAuthoringLease();
                }
            }
        }
    }, [apiKey, model, photos, scope, photoAnalysis, publishPhotoAssignmentReceipt]);

    // A retry button/tool invocation is deliberate operator intent. It may
    // resume a project that was opened in review mode or explicitly stopped,
    // whereas the evidence watcher below remains automatic and must never
    // clear either latch merely because a project was loaded.
    const retryPhotoAssignment = useCallback((options = { trigger: 'stage4-manual-retry' }) => {
        const liveScope = Array.isArray(scopeRef.current) ? scopeRef.current : [];
        if (Number(stageRef.current) !== 4 || !hasFinalStage3QuoteInputs({
            projectWMS: projectWMSRef.current,
            scope: liveScope,
            projectCostingSheet: projectCostingSheetRef.current,
        })) {
            return undefined;
        }
        aiPipelineHaltedRef.current = false;
        openedForReviewRef.current = false;
        return dispatchPhotoAutoAssign(options);
    }, [dispatchPhotoAutoAssign]);

    const getStage5PhotoGate = useCallback(({ explicitNavigation = false } = {}) => {
        const livePhotos = Array.isArray(photosRef.current) ? photosRef.current : [];
        const assignablePhotos = selectAssignablePhotos(livePhotos).photos;
        const liveScope = Array.isArray(scopeRef.current) ? scopeRef.current : [];
        const coverage = assignablePhotoCoverage(assignablePhotos, liveScope);
        const assessment = assessAttendedStage5PhotoGate({
            assignablePhotos,
            scope: liveScope,
            receipt: photoAssignmentReceiptRef.current,
            currentProjectId: currentProjectIdRef.current,
            currentLoadGeneration: projectLoadGenerationRef.current,
            photoAssignBusy: photoAssignBusyRef.current || photoReviewSaveInFlightRef.current,
            // Clicking NEXT is an explicit resume intent, like the manual
            // analysis/WMS/quote controls. Automatic authoring remains strict.
            pipelineHalted: explicitNavigation ? false : aiPipelineHaltedRef.current,
            // Opening NEXT is itself deliberate builder intent. Automatic quote
            // authoring still calls the strict assessment directly and remains
            // blocked while an existing project is merely open for review.
            openedForReview: explicitNavigation ? false : openedForReviewRef.current,
        });
        return Object.freeze({ ...assessment, ...coverage });
    }, []);

    const ensureStage5PhotoGate = useCallback(async ({
        allowUnchangedReviewAcceptance = false,
        trigger = 'stage4-manual-photo-completion',
    } = {}) => {
        const explicitNavigation = allowUnchangedReviewAcceptance === true;
        const before = getStage5PhotoGate({ explicitNavigation });
        if (before.ready || before.total === 0) return before;
        if (Number(stageRef.current) !== 4
            || photoAssignBusyRef.current
            || photoReviewSaveInFlightRef.current
            || (aiPipelineHaltedRef.current && !explicitNavigation)) return before;

        const assignmentRunContext = captureProjectRunContext({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const receipt = photoAssignmentReceiptRef.current;
        const livePhotos = Array.isArray(photosRef.current) ? photosRef.current : [];
        const assignablePhotos = selectAssignablePhotos(livePhotos).photos;
        const liveScope = Array.isArray(scopeRef.current) ? scopeRef.current : [];
        const coverage = assignablePhotoCoverage(assignablePhotos, liveScope);
        const fingerprint = photoAssignmentReceiptFingerprint(assignablePhotos, liveScope);
        const contextIsCurrent = () => (
            isProjectRunContextCurrent(assignmentRunContext, {
                projectId: currentProjectIdRef.current,
                loadGeneration: projectLoadGenerationRef.current,
                signal: projectAbortRef.current?.signal || null,
            })
            && paintedProjectLoadContextRef.current.projectId === assignmentRunContext.projectId
            && Number(paintedProjectLoadContextRef.current.loadGeneration) === assignmentRunContext.loadGeneration
        );
        if (!assignmentRunContext.projectId || !contextIsCurrent()) return before;
        if (!canPersistCompletedStage4PhotoReview({
            receipt,
            currentProjectId: assignmentRunContext.projectId,
            currentLoadGeneration: assignmentRunContext.loadGeneration,
            currentFingerprint: fingerprint,
            coverage,
            allowUnchangedReviewAcceptance,
        })) return before;

        // An explicit NEXT with zero linked photos is a deliberate builder
        // acceptance, not an unfinished AI attempt. Persist an exact,
        // byte-free evidence receipt on the existing scope JSON so reopen can
        // suppress another paid call for these photos while still recognising
        // a genuinely-added photo as new evidence. Passive automation cannot
        // mint this receipt.
        const scopeForSave = allowUnchangedReviewAcceptance && coverage.assigned === 0
            ? markAcceptedZeroScopePhotoReview(liveScope, assignablePhotos)
            : liveScope;
        const scopeForSaveFingerprint = scopeFingerprintForAutoDraft(scopeForSave);
        if (scopeForSave !== liveScope) {
            // Paint the invisible receipt before the async save so subsequent
            // builder edits descend from it. Settlement below still adopts a
            // canonical response only while this exact snapshot owns the lane.
            scopeRef.current = scopeForSave;
            setScope(scopeForSave);
        }

        photoReviewSaveInFlightRef.current = true;
        publishPhotoAssignmentReceipt({
            ...receipt,
            projectId: assignmentRunContext.projectId,
            loadGeneration: assignmentRunContext.loadGeneration,
            status: 'in-flight',
            fingerprint,
            assignedPhotos: coverage.assigned,
            assignablePhotos: coverage.total,
            trigger,
        });
        try {
            const savedProject = await saveCurrentProjectRef.current?.({
                scope: scopeForSave,
                silent: true,
            }, { runContext: assignmentRunContext });
            if (!savedProject || !contextIsCurrent()) {
                throw createStaleProjectRunError('Manual photo review save');
            }
            const savedScope = Array.isArray(savedProject.scope) ? savedProject.scope : [];
            const savedFingerprint = photoAssignmentReceiptFingerprint(assignablePhotos, savedScope);
            if (savedFingerprint !== fingerprint) {
                throw new Error('Manual photo review save did not preserve the exact scope links.');
            }
            const successResolution = resolvePhotoAssignmentSaveSuccess({
                liveScope: scopeRef.current,
                appliedScope: scopeForSave,
                appliedScopeFingerprint: scopeForSaveFingerprint,
                savedScope,
            });
            if (successResolution.shouldAdoptCanonical) {
                scopeRef.current = successResolution.scope;
                setScope(successResolution.scope);
            }
            const settledScope = successResolution.scope;
            const savedCoverage = assignablePhotoCoverage(assignablePhotos, settledScope);
            const savedDispositions = buildSavedPhotoDispositions({
                assignablePhotos,
                scopeItems: settledScope,
                proposedDispositions: receipt?.dispositions,
                unlinkedReason: 'builder-omitted',
            });
            publishPhotoAssignmentReceipt({
                projectId: assignmentRunContext.projectId,
                loadGeneration: assignmentRunContext.loadGeneration,
                status: 'saved',
                fingerprint: photoAssignmentReceiptFingerprint(assignablePhotos, settledScope),
                assignedPhotos: savedCoverage.assigned,
                assignablePhotos: savedCoverage.total,
                dispositions: savedDispositions,
                acceptedReviewReason: receipt?.reviewReason || receipt?.status || 'explicit-manual-coverage',
                trigger,
            });
            setNavMessage(prev => (
                prev?.id === 'photo-auto-assign-review'
                || prev?.id === 'photo-auto-assign-fallback'
                || prev?.id === 'photo-auto-assign-failed'
                    ? null
                    : prev
            ));
        } catch (error) {
            if (contextIsCurrent()) {
                const failureResolution = resolvePhotoAssignmentSaveFailure({
                    liveScope: scopeRef.current,
                    appliedScope: scopeForSave,
                    appliedScopeFingerprint: scopeForSaveFingerprint,
                    previousScope: liveScope,
                });
                if (failureResolution.shouldRollback) {
                    scopeRef.current = failureResolution.scope;
                    setScope(failureResolution.scope);
                }
                publishPhotoAssignmentReceipt({
                    projectId: assignmentRunContext.projectId,
                    loadGeneration: assignmentRunContext.loadGeneration,
                    status: 'save-failed',
                    fingerprint: photoAssignmentReceiptFingerprint(assignablePhotos, failureResolution.scope),
                    assignedPhotos: assignablePhotoCoverage(assignablePhotos, failureResolution.scope).assigned,
                    assignablePhotos: coverage.total,
                    reviewReason: receipt?.reviewReason || receipt?.status || 'explicit-manual-coverage',
                    preservedLiveEdits: failureResolution.preservedLiveEdits,
                    trigger,
                });
                setNavMessage({
                    id: 'photo-auto-assign-failed',
                    tone: 'error',
                    icon: 'AlertTriangle',
                    text: 'Stage 5 remains available; only the completed photo links are not durably saved. Retry from Stage 4 when storage recovers.',
                    dismissible: true,
                });
            }
            console.warn(`[photo-auto-assign] MANUAL REVIEW SAVE FAILED (${trigger}):`, error?.message || error);
        } finally {
            photoReviewSaveInFlightRef.current = false;
        }
        return getStage5PhotoGate({ explicitNavigation });
    }, [getStage5PhotoGate, publishPhotoAssignmentReceipt]);

    return {
        dispatchPhotoAutoAssign,
        retryPhotoAssignment,
        getStage5PhotoGate,
        ensureStage5PhotoGate,
    };
}
